import { useSearchParams } from "react-router";
import { Button, Spinner, Text } from "@chakra-ui/react";
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogCloseTrigger,
} from "@/components/ui/dialog";

import { invitationRoleSuffix } from "@entities/invitation";
import { useInvitationPreviewQuery } from "@features/invitation/preview";
import { useAuthenticationContext } from "@app/auth/use-authentication-context";
import { useRespondToInvitationMutation } from "../../respond/lib/use-respond-to-invitation-mutation";

const INVITATION_TOKEN_PARAM = "invitationToken";

interface InvitationPromptProps {
  token: string;
  onClose: () => void;
}

export const InvitationPrompt: React.FC<InvitationPromptProps> = ({
  token,
  onClose,
}) => {
  const { email, revalidateSession } = useAuthenticationContext();
  const { data: preview, isLoading } = useInvitationPreviewQuery(token);

  const { mutate, isPending } = useRespondToInvitationMutation({
    onSuccess: (_, { action }) => {
      if (action === "accept") revalidateSession();
      onClose();
    },
  });

  const isOpen = Boolean(
    !isLoading &&
      preview?.status === "valid" &&
      preview.email.toLowerCase() === email.toLowerCase(),
  );

  if (!isOpen || !preview) return null;

  return (
    <DialogRoot
      open
      onOpenChange={({ open }) => {
        if (!open) onClose();
      }}
      placement="center"
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Invitation to join {preview.companyName}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Text>
            You have been invited to join{" "}
            <Text as="span" fontWeight="bold">
              {preview.companyName}
            </Text>
            {invitationRoleSuffix(preview.role)}. Would you like to accept the
            invitation?
          </Text>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => mutate({ token, action: "decline" })}
            disabled={isPending}
          >
            Decline
          </Button>
          <Button
            colorPalette="teal"
            onClick={() => mutate({ token, action: "accept" })}
            disabled={isPending}
          >
            {isPending ? <Spinner size="xs" /> : "Accept"}
          </Button>
        </DialogFooter>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  );
};

export const InvitationAcceptPrompt: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get(INVITATION_TOKEN_PARAM);

  if (!token) return null;

  const close = () => {
    searchParams.delete(INVITATION_TOKEN_PARAM);
    setSearchParams(searchParams, { replace: true });
  };

  return <InvitationPrompt token={token} onClose={close} />;
};
